import React, { FC, useCallback, useEffect, useState } from 'react';

import { CloudAccountInfoGetResponse } from '@ariksa/jit/api';
import { Box, Center, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { INIT_PAGE_INFO } from 'services/utils/constants';
import { customTheme } from 'theme';
import { Optional } from 'types/utils';

import {
  Card,
  PageHeaderWithIcon,
  WithResourceIcon,
} from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import {
  ActionButton,
  AddButton,
  BackIconButton,
  DeleteActionButton,
} from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import {
  EditIcon,
  HighRiskIcon,
  IconTypes,
  ThumbsUpIcon,
} from 'components/Icons';
import { PageInfo } from 'components/Navigation';
import { selectJitSetup } from 'containers/Jit/Setup/selectors';
import { actions } from 'containers/Jit/Setup/slice';

export const ApprovalPolicies: FC = () => {
  const dispatch = useDispatch();
  const jitRoleConfig = useDisclosure();
  const configureApproval = useDisclosure();
  const { approvalPolicies } = useSelector(selectJitSetup);
  const [activeAccount, setActiveAccount] = useState<
    Optional<CloudAccountInfoGetResponse>
  >(null);
  const navigate = useNavigate();
  const { setLearnMore } = useKnowledgeBase();

  const getJitApprovalPolicies = useCallback(
    (info: PageInfo = INIT_PAGE_INFO) => {
      dispatch(
        actions.getApprovalPolicies({
          q: {},
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    getJitApprovalPolicies();
  }, [getJitApprovalPolicies]);

  useEffect(() => {
    if (jitRoleConfig.isOpen && activeAccount) {
      dispatch(actions.getRoles({ q: { accountId: activeAccount.uuid } }));
    }
  }, [dispatch, activeAccount, jitRoleConfig.isOpen]);

  const handleDelete = useCallback(
    row => {
      dispatch(
        actions.deleteApprovalPolicy({
          q: {
            policyId: row.id,
          },
          onSuccess: () => {
            getJitApprovalPolicies();
          },
        }),
      );
    },
    [dispatch, getJitApprovalPolicies],
  );

  const columns = [
    {
      header: <Box pl={9}>Name</Box>,
      render: ({ row }) => (
        <WithResourceIcon
          resourceType={IconTypes.Policy}
          color={customTheme.colors.green['300']}
        >
          {row?.name}
        </WithResourceIcon>
      ),
    },
    {
      header: 'Cloud',
      accessor: 'provider',
      /*render: ({ row }) => (
        <HStack>
          <Box boxSize={5} color={customTheme.colors.gray['250']}>
            <Center>
              <EnvironmentIcon />
            </Center>
          </Box>
          <Box>{accountEnvironmentMapping?.[row?.uuid]?.name}</Box>
        </HStack>
      ),*/
      align: 'left',
    },
    {
      header: 'Description',
      accessor: 'description',
      align: 'left',
    },
    {
      header: 'Accounts',
      accessor: '',
      align: 'left',
    },
    {
      header: 'Access Duration',
      accessor: '',
      align: 'left',
    },
    {
      header: 'Approval',
      accessor: '',
      align: 'left',
    },
    {
      header: 'Actions',
      //ce: 'right',
      render: ({ row }) => (
        <Center onClick={e => e.stopPropagation()}>
          <ActionButton
            label="Edit"
            icon={<EditIcon />}
            onClick={() => {
              navigate('/jit/setup/approval-policy/edit/' + row.id);
            }}
          />
          <DeleteActionButton
            onConfirm={() => {
              handleDelete(row);
            }}
            name="CONFIRM"
            confirmationText={`Please type "CONFIRM" to delete.`}
            icon={<HighRiskIcon />}
            label="Disable JIT"
          />
        </Center>
      ),
    },
  ];

  return (
    <Card>
      <Stack spacing={5} h="full" w="full">
        <HStack
          color={customTheme.colors.gray['250']}
          cursor="pointer"
          onClick={() => navigate('/jit/setup')}
        >
          <Box>
            <BackIconButton color={customTheme.colors.gray['250']} />
          </Box>
          <Box>Back to JIT Configuration</Box>
        </HStack>
        <HStack justify="space-between">
          <Box>
            <PageHeaderWithIcon
              label="Approval Policies For JIT"
              icon={<ThumbsUpIcon />}
            />
          </Box>
          <HStack>
            <LearnMoreButton
              onOpen={() => setLearnMore('jit_approvals')}
              label={'JIT Approvals '}
            />
            <AddButton
              label="Add Approval Policy"
              onClick={() => navigate('/jit/setup/approval-policy/add')}
            />
          </HStack>
        </HStack>
        <Box flex={1}>
          <Table
            columns={columns}
            data={approvalPolicies.data}
            isLoading={approvalPolicies.isLoading}
            /*pagination={{
            totalCount: jitAccounts.page.totalCount,
            pageInfo: jitAccounts.page.info,
            onChange(info: PageInfo) {
              getJitAccounts(info);
            },
          }}*/
            onRowClick={row => {
              setActiveAccount(row);
              // metadataDrawer.onOpen();
            }}
          />
        </Box>
      </Stack>
      <LearnMore />
    </Card>
  );
};
